import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, KeyRound, ShieldCheck, Link2, Unlink, Trash2, Plus, Pencil, Lock, LogOut } from "lucide-react";

const CRIMSON = "#bf3b4f";

export function RolePicker({ catalog, value, onChange }) {
  const groups = catalog.reduce((a, r) => ((a[r.group] = a[r.group] || []).push(r), a), {});
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="space-y-3" data-testid="role-picker">
      {Object.entries(groups).map(([group, roles]) => (
        <div key={group}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{group}</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {roles.map((r) => {
              const on = value.includes(r.value);
              return (
                <button key={r.value} type="button" onClick={() => toggle(r.value)}
                  data-testid={`role-${r.value.replace(/\s+/g, "-")}`}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-[#bf3b4f] bg-[#bf3b4f]/10 text-[#bf3b4f] font-medium" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function UserDialog({ open, onClose, catalog, editing, onSaved }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [roles, setRoles] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setEmail(editing?.email || ""); setFullName(editing?.full_name || ""); setRoles(editing?.frappe_roles || []); }
  }, [open, editing]);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) { await api.authUpdateUser(editing.email, { full_name: fullName, roles }); toast.success("User updated."); }
      else { await api.authCreateUser({ email, full_name: fullName, roles }); toast.success("User created. They'll set a password via Forgot password."); }
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save."); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="user-dialog">
        <DialogHeader><DialogTitle className="font-brand">{editing ? "Edit user" : "Create user"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-semibold text-slate-700">Email</Label>
            <Input value={email} disabled={!!editing} onChange={(e) => setEmail(e.target.value)} placeholder="name@prydessupport.com" className="mt-1" data-testid="user-email" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700">Full name</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Full name" className="mt-1" data-testid="user-fullname" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-700 block">Roles &amp; access</Label>
            <p className="text-[11px] text-slate-400 mb-2">Pick every role this person needs; they get everything those roles allow. Change what a role allows in <a href="/settings/roles" className="underline hover:text-slate-600">Roles &amp; Permissions</a>.</p>
            <RolePicker catalog={catalog} value={roles} onChange={setRoles} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="user-cancel">Cancel</Button>
          <Button style={{ backgroundColor: CRIMSON }} onClick={save} disabled={saving || (!editing && !email)} data-testid="user-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Save" : "Create")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkDialog({ open, onClose, email, employees, onLinked }) {
  const [emp, setEmp] = useState("");
  const [q, setQ] = useState("");
  const filtered = employees.filter((e) => (e.display_name || e.name).toLowerCase().includes(q.toLowerCase()));
  const link = async () => {
    try { await api.authLinkUser(email, emp); toast.success("Linked to HR profile."); onLinked(); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed."); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="link-dialog">
        <DialogHeader><DialogTitle className="font-brand">Link {email} to an HR profile</DialogTitle></DialogHeader>
        <Input placeholder="Search staff…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="link-search" />
        <div className="max-h-60 overflow-auto space-y-1 mt-2">
          {filtered.map((e) => (
            <button key={e.name} onClick={() => setEmp(e.name)} data-testid={`link-emp-${e.name}`}
              className={`w-full text-left rounded-lg border px-3 py-2 text-sm ${emp === e.name ? "border-[#bf3b4f] bg-[#bf3b4f]/5" : "border-slate-200"}`}>
              {e.display_name || e.name} <span className="text-slate-400 text-xs">· {e.name}</span>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button style={{ backgroundColor: CRIMSON }} disabled={!emp} onClick={link} data-testid="link-confirm">Link</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function UsersManager() {
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dialog, setDialog] = useState(null);   // {editing} | "new"
  const [linkFor, setLinkFor] = useState(null);

  const load = () => api.authUsers().then((d) => setUsers(d.users || [])).catch(() => {});
  useEffect(() => {
    load();
    api.authRolesCatalog().then((d) => setCatalog(d.roles || [])).catch(() => {});
    api.hrStaffList().then((d) => setEmployees(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed."); } };
  const roleLabels = (rs) => rs.map((r) => (catalog.find((c) => c.value === r)?.label || r)).join(", ") || "—";

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="users-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold font-brand text-slate-800">Users</h3>
        <Button size="sm" style={{ backgroundColor: CRIMSON }} onClick={() => setDialog("new")} data-testid="create-user-btn"><Plus className="h-4 w-4 mr-1.5" />Create user</Button>
      </div>
      <div className="space-y-2">
        {users.map((u) => (
          <div key={u.email} className="rounded-lg border border-slate-200 p-3" data-testid={`user-row-${u.email}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-slate-800">{u.full_name}</span>
                  {u.hr_linked && <Badge className="border bg-sky-50 text-sky-700 border-sky-200"><Link2 className="h-3 w-3 mr-1" />HR: {u.employee}</Badge>}
                  {!u.enabled && <Badge className="border bg-slate-100 text-slate-500 border-slate-200">Disabled</Badge>}
                  {u.must_reset_password && <Badge className="border bg-amber-50 text-amber-700 border-amber-200">Awaiting password</Badge>}
                  {u.twofa_method && <Badge className="border bg-emerald-50 text-emerald-700 border-emerald-200"><ShieldCheck className="h-3 w-3 mr-1" />2FA</Badge>}
                </div>
                <p className="text-xs text-slate-500 truncate">{u.email}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{roleLabels(u.frappe_roles)}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Button size="sm" variant="outline" title="Send set-password link" onClick={() => act(() => api.authResetUserPw(u.email), "Set-password link sent.")} data-testid={`reset-pw-${u.email}`}><KeyRound className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" title="Reset 2FA" onClick={() => act(() => api.authResetUser2fa(u.email), "2FA reset — they'll re-enroll next sign-in.")} data-testid={`reset-2fa-${u.email}`}><ShieldCheck className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" title="Sign out everywhere" onClick={() => window.confirm(`Sign ${u.email} out on every device now?`) && act(() => api.authSignOutUser(u.email), "Signed out on every device.")} data-testid={`sign-out-${u.email}`}><LogOut className="h-4 w-4" /></Button>
                {u.hr_linked ? (
                  <Button size="sm" variant="outline" title="Unlink from HR" onClick={() => act(() => api.authUnlinkUser(u.email), "Unlinked.")} data-testid={`unlink-${u.email}`}><Unlink className="h-4 w-4" /></Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" title="Edit" onClick={() => setDialog({ editing: u })} data-testid={`edit-${u.email}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" title="Link to HR profile" onClick={() => setLinkFor(u.email)} data-testid={`link-${u.email}`}><Link2 className="h-4 w-4" /></Button>
                    <Button size="sm" variant="outline" className="text-rose-600" title="Delete" onClick={() => window.confirm(`Delete ${u.email}?`) && act(() => api.authDeleteUser(u.email), "User deleted.")} data-testid={`delete-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                  </>
                )}
              </div>
            </div>
            {u.hr_linked && <p className="mt-1.5 text-[11px] text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" />Name &amp; email sync from HR. Only password / 2FA reset available here — manage roles from the staff record.</p>}
          </div>
        ))}
        {users.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No users yet. Use the Seed function above, or create one manually.</p>}
      </div>

      <UserDialog open={dialog === "new" || !!dialog?.editing} editing={dialog?.editing} catalog={catalog}
        onClose={() => setDialog(null)} onSaved={load} />
      <LinkDialog open={!!linkFor} email={linkFor} employees={employees} onClose={() => setLinkFor(null)} onLinked={load} />
    </div>
  );
}
